import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppClassicModule } from 'app-classic/app-classic.module';
import { LDClient, initialize, type LDSingleKindContext } from 'launchdarkly-js-client-sdk';
import { AppModule } from './app/app.module';
import { APP_CONFIG, CONFIG_URL } from './app/services/config/constants';

async function fetchSiteData(
    dsbApiUrl: string,
    dsbApiSecretKey: string,
    daySmartAccountID: string
): Promise<{ supportsPets: boolean; bookingURL: string }> {
    const response = await fetch(`${dsbApiUrl}/api/site/daySmartAccountID/${encodeURIComponent(daySmartAccountID)}`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            'x-api-key': dsbApiSecretKey,
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch site data: ${response.status}`);
    }

    const result = await response.json();
    return result;
}

function initializeLaunchDarkly(apiKey: string, dsAccountId: string, supportsPets?: boolean): Promise<LDClient> {
    return new Promise<LDClient>((resolve, reject) => {
        if (!apiKey) {
            reject('Missing LaunchDarkly API key');
            return;
        }

        const context: LDSingleKindContext & { supportsPets: boolean } = {
            kind: 'user',
            key: dsAccountId,
            supportsPets: supportsPets ?? false,
        };

        const client = initialize(apiKey, context);

        client
            .waitUntilReady()
            .then(() => resolve(client))
            .catch((error) => {
                console.error('LaunchDarkly client failed to initialize:', error);
                reject(error);
            });
    });
}

function configLoaded(this: XMLHttpRequest) {
    try {
        const configuration = JSON.parse(this.responseText);
        if (configuration?.production) {
            enableProdMode();
        }
        const url = new URL(window.location.href);
        let daySmartAccountID = [...url.searchParams].find(([k]) => k.toLowerCase() === 'dsid')?.[1] ?? null;

        // If DSID is missing, handle error case
        if (!daySmartAccountID) {
            console.warn('Missing DSID query parameter, loading error page');

            if (window.location.pathname.includes('/booking/invalid')) {
                const linkElement = document.createElement('link');
                linkElement.rel = 'stylesheet';
                linkElement.href = 'styles-classic.css';
                document.head.appendChild(linkElement);

                platformBrowserDynamic([{ provide: APP_CONFIG, useValue: configuration }])
                    .bootstrapModule(AppClassicModule)
                    .catch((err) => console.error(err));
            } else {
                window.location.replace('/booking/invalid');
            }
            return;
        } else {
            daySmartAccountID = daySmartAccountID.trim().toUpperCase();
        }

        // Fetch site data before initializing LaunchDarkly
        fetchSiteData(configuration.dsbApiUrl, configuration.dsbApiSecretKey, daySmartAccountID)
            .then((siteData) => {
                localStorage.setItem('site', JSON.stringify(siteData));

                // Initialize LaunchDarkly with supportsPets
                return initializeLaunchDarkly(configuration.launchdarklyApiKey, daySmartAccountID, siteData.supportsPets);
            })
            .then((ldClient) => {
                const useRedesign = ldClient.variation('redesign', false);

                // decide which stylesheet to load
                // when app-classic is removed, please update angular.json to change the "styles" array from:
                /* "styles": [
                        {
                            "input": "src/styles.scss",
                            "inject": false,
                            "bundleName": "styles"
                        }
                    ],
                */
                // to:
                // "styles": ["src/styles.scss"],
                // which will also automatically insert the <link> tag so we don't have to do it manually here anymore
                const stylesheet = useRedesign ? 'styles' : 'styles-classic';
                const linkElement = document.createElement('link');
                linkElement.rel = 'stylesheet';
                linkElement.href = `${stylesheet}.css`;
                document.head.appendChild(linkElement);

                // pass config to bootstrap process using an injection token
                if (useRedesign) {
                    platformBrowserDynamic([{ provide: APP_CONFIG, useValue: configuration }])
                        .bootstrapModule(AppModule)
                        .catch((err) => console.error(err));
                } else {
                    platformBrowserDynamic([{ provide: APP_CONFIG, useValue: configuration }])
                        .bootstrapModule(AppClassicModule)
                        .catch((err) => console.error(err));
                }
            })
            .catch((err) => {
                console.error('Error during initialization (site fetch or LaunchDarkly):', err);
                // Fallback to classic version if site fetch or LaunchDarkly fails
                const linkElement = document.createElement('link');
                linkElement.rel = 'stylesheet';
                linkElement.href = 'styles-classic.css';
                document.head.appendChild(linkElement);

                platformBrowserDynamic([{ provide: APP_CONFIG, useValue: configuration }])
                    .bootstrapModule(AppClassicModule)
                    .catch((err) => console.error(err));
            });
    } catch (error) {
        console.error('Error in configLoaded', error);
        console.error(error);
    }
}

function configFailed(evt: any) {
    console.error('Error: retrieving config.json', evt);
}
const request = new XMLHttpRequest();
request.addEventListener('load', configLoaded);
request.addEventListener('error', configFailed);
request.open('GET', CONFIG_URL);
request.send();
